// The spring maths every settle in the app is built from — pure, no DOM and no engine.

import { TRANSITION_CONFIG } from './motion.config'

const { spring } = TRANSITION_CONFIG

// Not a frame rate: the compositor interpolates a straight line between samples, so sampling at 60Hz hands a
// 120Hz display piecewise-linear corners at exactly the rate it can resolve. A ~350ms settle is ~84 keyframes.
export const SPRING_HZ = 240
/** Hard cap on a settle (3s at SPRING_HZ) — a spring that hasn't arrived by then never will. */
const SPRING_MAX_STEPS = SPRING_HZ * 3

// Settle on POSITION, not velocity: the tail of a near-critically-damped spring hovers with tiny residual
// velocity for hundreds of ms, and for a REVERSE the top pane sits AT the covering position for all of it —
// which reads as a frozen screen. Lives here so the composited path and the rAF fallback stop at the same place.
export const SETTLE_EPSILON = 0.01

/** One step of the spring, shared by the ahead-of-time sampler and the main-thread fallback integrator. */
export function springStep(progress: number, velocity: number, target: number, dt: number): SpringState {
  const accel = (-spring.stiffness * (progress - target) - spring.damping * velocity) / spring.mass
  const v = velocity + accel * dt
  return { progress: progress + v * dt, velocity: v }
}

export interface SpringState {
  readonly progress: number
  readonly velocity: number
}

// One definition, so a trajectory's POSITION and its VELOCITY can never be read off different indices.
export function sampleIndexAt(samples: readonly number[], elapsedMs: number): number {
  const index = Math.round((elapsedMs / 1000) * SPRING_HZ)
  return index < 0 ? 0 : index > samples.length - 1 ? samples.length - 1 : index
}

// A settle is not interactive, so its whole trajectory is known up front and can go to the compositor, where a
// concurrent React commit cannot starve it.
// Sampled keyframes rather than a linear() spring easing: linear() is too new for some browsers this app ships to.
export function springSamples(from: number, target: number, velocity: number): number[] {
  const dt = 1 / SPRING_HZ
  const samples: number[] = [from]
  let p = from
  let v = velocity
  for (let i = 0; i < SPRING_MAX_STEPS; i++) {
    ;({ progress: p, velocity: v } = springStep(p, v, target, dt))
    if (Math.abs(p - target) < SETTLE_EPSILON) break
    samples.push(p)
  }
  samples.push(target)
  return samples
}

// Integrated ONCE at module load: one array is what guarantees the surface engine, the nav stack and the sheet
// move on the SAME curve, each time-scaling it into its own duration.
export const SPRING_TRAJECTORY: readonly number[] = springSamples(0, 1, 0)

// Read back from the animation's OWN clock, never computed style: one source of truth and no layout read, so a
// re-grab hands a baseline that exactly matches the pixels.
export function springProgressAt(lead: Animation | undefined, samples: readonly number[], fallback: number): number {
  if (!lead || samples.length === 0) return fallback
  return samples[sampleIndexAt(samples, Number(lead.currentTime ?? 0))] ?? fallback
}
